import { useCallback } from 'react';

import type { UseFormReturn } from 'react-hook-form';

import type {
  CHANGE_SET_EXTRACTOR_TYPE,
  NameConverter,
  OnRevert,
  PivotClassifier,
  PivotValueCalculator,
  QueryToFormConverter,
  ValueConverter,
} from '@/_containers';
import { historyItemStringConverter, newValueCalculator } from '@/_containers';
import type { OnUpdate } from '@/_schemas/types';
import { resetFormState } from '@/_shared';

import { invalidateAgreementQuery } from '../apis';
import { agreementFormToUpdate, agreementQueryToForm, fieldNames } from '../converters';
import type { AgreementFormSchema, AgreementQuerySchema, AgreementUpdateSchema } from '../schemas';

const DATES = ['agreement_date', 'expiry_date'];

type FilteredAgreementFormSchema = Omit<AgreementFormSchema, 'tempAgreement_deliverables'>;

export function useRevisionHistory(
  form: UseFormReturn<AgreementFormSchema>,
  onUpdate: OnUpdate<AgreementUpdateSchema, { data: AgreementQuerySchema }>
) {
  const validatePivot = useCallback((field: keyof FilteredAgreementFormSchema) => {
    return field === 'emergency_contact'; // || field === 'primary_domains';
  }, []);

  const onRevert: OnRevert<FilteredAgreementFormSchema> = useCallback(
    async (oldValue, field) => {
      const allFormValues = form.getValues();
      let nV: AgreementUpdateSchema = {} as AgreementUpdateSchema;
      const isPivot = !!field && validatePivot(field);

      if (!isPivot) {
        nV = agreementFormToUpdate({ ...allFormValues, ...oldValue });
      } else {
        nV = agreementFormToUpdate({ ...allFormValues });
        nV = { ...nV, [field as keyof AgreementUpdateSchema]: oldValue };
      }

      const res = await onUpdate(nV, 'Agreement changes have been reverted.', true, false);
      if (res.data) {
        if (isPivot) {
          invalidateAgreementQuery(nV.id + '');
        } else {
          resetFormState(form, { ...allFormValues, ...oldValue });
        }
      }
    },
    [form, onUpdate, validatePivot]
  );

  const queryToFormConverter: QueryToFormConverter<AgreementQuerySchema, FilteredAgreementFormSchema> = useCallback(
    (q) => {
      return agreementQueryToForm({ ...q });
    },
    []
  );

  const nameConverter: NameConverter<FilteredAgreementFormSchema> = useCallback((f) => {
    // if key does not exist in fieldNames or field is not revertible
    if (!(typeof fieldNames[f] !== 'undefined' && fieldNames[f] !== null) || !fieldNames[f].revertible) return null;
    return fieldNames[f].name;
  }, []);

  const valueConverter: ValueConverter<FilteredAgreementFormSchema> = useCallback((f, form) => {
    // if key does not exist in fieldNames or field is not revertible
    const v = form[f];
    if (
      typeof fieldNames[f] === 'undefined' ||
      fieldNames[f] === null ||
      !fieldNames[f].revertible ||
      v === null ||
      typeof v === 'undefined'
    ) {
      return null;
    }

    if (typeof v === 'object') {
      if ('name' in v) {
        return v.name;
      }
      return f;
    } else if (typeof v === 'string') {
      return historyItemStringConverter(v, DATES.includes(f));
    } else if (typeof v === 'number') {
      return v + '';
    }

    return null;
  }, []);

  const pivotClassifier: PivotClassifier<FilteredAgreementFormSchema> = useCallback(
    (f) => {
      const isPivot = validatePivot(f);
      let changeSetExtractorType: CHANGE_SET_EXTRACTOR_TYPE = null;
      if (isPivot) {
        switch (f) {
          case 'emergency_contact':
            changeSetExtractorType = 'FIRST_NAME_LAST_NAME';
            break;
        }
      }
      return { isPivot, changeSetExtractorType };
    },
    [validatePivot]
  );

  const pivotValueCalculator: PivotValueCalculator<FilteredAgreementFormSchema> = useCallback(
    (field, items) => {
      const allFormValues = form.getValues();
      if (validatePivot(field)) {
        const nV = agreementFormToUpdate({ ...allFormValues });
        let calcValue = nV[field];

        items.forEach(([value, type]) => {
          if (value !== null && typeof value !== 'undefined') {
            const oldValueIds = value[field]?.map((item) => item.id) ?? [];
            calcValue = newValueCalculator(oldValueIds, calcValue, type);
          }
        });
        return calcValue;
      }
      return [];
    },
    [form, validatePivot]
  );

  return {
    queryToFormConverter,
    nameConverter,
    valueConverter,
    onRevert,
    validatePivot,
    pivotClassifier,
    pivotValueCalculator,
  };
}
